import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { 
  Lock, 
  KeyRound, 
  Mail, 
  Phone,
  Copy, 
  Check, 
  Download, 
  Trash2, 
  Edit3,
  UserPlus, 
  Search, 
  Users, 
  Baby,
  UserCheck,
  Calendar,
  MapPin,
  Home,
  LogOut,
  AlertCircle,
  FileSpreadsheet,
  Filter,
  CheckCircle2,
  X
} from "lucide-react";

const STORAGE_KEY = "kongeengen_event_registrations";
const AUTH_KEY = "kongeengen_newsletter_authenticated";
const CORRECT_PASSWORD = "kongeengen123!"; // Same board password

// Initial seed registrations
const DEFAULT_REGISTRATIONS = [
  {
    id: "ev-1",
    name: "Kern Holm Thomsen",
    email: "Kongeengen@gmail.com",
    phone: "2255-8863",
    address: "Kongevænget 84",
    eventTitle: "Sommerfest 2026",
    eventId: "1",
    eventDate: "2026-08-15",
    adults: 2,
    children: 2,
    date: "2026-06-10",
    source: "Hjemmeside"
  },
  {
    id: "ev-2",
    name: "Mikael Skrydstrup",
    email: "mikael@kongstedgaards.dk",
    phone: "3122-4455",
    address: "Kongstedgårds Alle 12",
    eventTitle: "Sommerfest 2026",
    eventId: "1",
    eventDate: "2026-08-15",
    adults: 2,
    children: 3,
    date: "2026-06-14",
    source: "Hjemmeside"
  },
  {
    id: "ev-3",
    name: "Morten Jessen",
    email: "morten.jessen@gmail.com",
    phone: "4011-2233",
    address: "Kongevænget 18",
    eventTitle: "Sommerfest 2026",
    eventId: "1",
    eventDate: "2026-08-15",
    adults: 2,
    children: 1,
    date: "2026-06-20",
    source: "Hjemmeside"
  },
  {
    id: "ev-4",
    name: "Sofie Amalie Berg",
    email: "sofie.berg@gmail.com",
    phone: "5166-7788",
    address: "Kongevænget 42",
    eventTitle: "Sommerfest 2026",
    eventId: "1",
    eventDate: "2026-08-15",
    adults: 1,
    children: 2,
    date: "2026-07-02",
    source: "Hjemmeside"
  },
  {
    id: "ev-5",
    name: "Christian Toft Madsen",
    email: "kasserer@kongeengen.dk",
    phone: "2033-4455",
    address: "Kongevænget 3",
    eventTitle: "Fælles Arbejdsdag",
    eventId: "2",
    eventDate: "2026-09-05",
    adults: 2,
    children: 0,
    date: "2026-07-15",
    source: "Hjemmeside"
  },
  {
    id: "ev-6",
    name: "Daniel Haurum Kristensen",
    email: "daniel.haurum@outlook.com",
    phone: "6088-9900",
    address: "Kongevænget 65",
    eventTitle: "Fælles Arbejdsdag",
    eventId: "2",
    eventDate: "2026-09-05",
    adults: 2,
    children: 1,
    date: "2026-07-18",
    source: "Hjemmeside"
  },
  {
    id: "ev-7",
    name: "Lars Henrik Nørgaard",
    email: "lh.noergaard@privat.dk",
    phone: "2877-6655",
    address: "Kongstedgårds Alle 5",
    eventTitle: "Fælles Arbejdsdag",
    eventId: "2",
    eventDate: "2026-09-05",
    adults: 1,
    children: 0,
    date: "2026-07-22",
    source: "Hjemmeside"
  },
  {
    id: "ev-8",
    name: "Sofie Amalie Berg",
    email: "sofie.berg@gmail.com",
    phone: "5166-7788",
    address: "Kongevænget 42",
    eventTitle: "Fastelavnsfest",
    eventId: "3",
    eventDate: "2026-02-15",
    adults: 2,
    children: 3,
    date: "2026-01-20",
    source: "Hjemmeside"
  }
];

export function getStoredEventRegistrations() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_REGISTRATIONS));
    return DEFAULT_REGISTRATIONS;
  } catch (err) {
    console.error("Error reading event registrations from localStorage", err);
    return DEFAULT_REGISTRATIONS;
  }
}

export function saveEventRegistration(data) {
  try {
    const current = getStoredEventRegistrations();
    const newEntry = {
      id: "ev-" + Date.now().toString(),
      name: data.name || "Uden navn",
      email: data.email || "",
      phone: data.phone || "",
      address: data.address || "",
      eventTitle: data.eventTitle || data.event_title || "Arrangement",
      eventId: data.eventId || data.event_id || "",
      eventDate: data.eventDate || data.event_date || "",
      adults: parseInt(data.adults, 10) || 1,
      children: parseInt(data.children, 10) || 0,
      date: new Date().toISOString().split("T")[0],
      source: "Hjemmeside"
    };

    const updated = [newEntry, ...current];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    window.dispatchEvent(new Event("event_registrations_updated"));
    return true;
  } catch (err) {
    console.error("Error saving event registration:", err);
    return false;
  }
}

export default function EventRegistrations() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [authError, setAuthError] = useState(false);

  const [registrations, setRegistrations] = useState([]);
  const [selectedEventFilter, setSelectedEventFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedFormat, setCopiedFormat] = useState(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Form state for Add/Edit
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    eventTitle: "Sommerfest 2026",
    adults: 1,
    children: 0
  });

  // Load auth status & registrations
  useEffect(() => {
    const isAuth = sessionStorage.getItem(AUTH_KEY) === "true";
    if (isAuth) {
      setIsAuthenticated(true);
    }

    const loadData = () => {
      setRegistrations(getStoredEventRegistrations());
    };

    loadData();

    window.addEventListener("event_registrations_updated", loadData);
    window.addEventListener("storage", loadData);
    return () => {
      window.removeEventListener("event_registrations_updated", loadData);
      window.removeEventListener("storage", loadData);
    };
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (passwordInput.trim().toLowerCase() === CORRECT_PASSWORD.toLowerCase()) {
      setIsAuthenticated(true);
      setAuthError(false);
      sessionStorage.setItem(AUTH_KEY, "true");
    } else {
      setAuthError(true);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem(AUTH_KEY);
    setPasswordInput("");
  };

  // Distinct events list for filter dropdown
  const eventOptions = useMemo(() => {
    const set = new Set();
    registrations.forEach(r => {
      if (r.eventTitle) set.add(r.eventTitle);
    });
    return Array.from(set);
  }, [registrations]);

  // Filtered registrations
  const filteredRegistrations = useMemo(() => {
    return registrations.filter((item) => {
      const matchesEvent = selectedEventFilter === "all" || item.eventTitle === selectedEventFilter;
      const term = searchTerm.toLowerCase().trim();
      const matchesSearch = !term || (
        item.name.toLowerCase().includes(term) ||
        item.email.toLowerCase().includes(term) ||
        item.phone.toLowerCase().includes(term) ||
        item.address.toLowerCase().includes(term) ||
        item.eventTitle.toLowerCase().includes(term)
      );
      return matchesEvent && matchesSearch;
    });
  }, [registrations, selectedEventFilter, searchTerm]);

  // Dynamic sums for the current view
  const stats = useMemo(() => {
    let totalAdults = 0;
    let totalChildren = 0;
    filteredRegistrations.forEach((r) => {
      totalAdults += parseInt(r.adults, 10) || 0;
      totalChildren += parseInt(r.children, 10) || 0;
    });
    return {
      totalRegistrations: filteredRegistrations.length,
      totalAdults,
      totalChildren,
      totalPersons: totalAdults + totalChildren
    };
  }, [filteredRegistrations]);

  const handleDelete = (id) => {
    if (window.confirm("Er du sikker på, at du vil slette denne tilmelding?")) {
      const updated = registrations.filter(r => r.id !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      setRegistrations(updated);
      window.dispatchEvent(new Event("event_registrations_updated"));
    }
  };

  const handleOpenAdd = () => {
    setEditingItem(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      address: "",
      eventTitle: eventOptions[0] || "Sommerfest 2026",
      adults: 1,
      children: 0
    });
    setShowAddModal(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      email: item.email,
      phone: item.phone,
      address: item.address,
      eventTitle: item.eventTitle,
      adults: item.adults,
      children: item.children
    });
    setShowAddModal(true);
  };

  const handleSaveModal = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;

    if (editingItem) {
      // Update existing
      const updated = registrations.map(r => {
        if (r.id === editingItem.id) {
          return {
            ...r,
            name: formData.name.trim(),
            email: formData.email.trim(),
            phone: formData.phone.trim(),
            address: formData.address.trim(),
            eventTitle: formData.eventTitle.trim(),
            adults: parseInt(formData.adults, 10) || 0,
            children: parseInt(formData.children, 10) || 0
          };
        }
        return r;
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      setRegistrations(updated);
    } else {
      // Add new
      const newEntry = {
        id: "ev-" + Date.now().toString(),
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        eventTitle: formData.eventTitle.trim(),
        adults: parseInt(formData.adults, 10) || 1,
        children: parseInt(formData.children, 10) || 0,
        date: new Date().toISOString().split("T")[0],
        source: "Manuelt tilføjet"
      };
      const updated = [newEntry, ...registrations];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      setRegistrations(updated);
    }

    window.dispatchEvent(new Event("event_registrations_updated"));
    setShowAddModal(false);
    setEditingItem(null);
  };

  // Copy emails
  const handleCopyEmails = (separator) => {
    const emails = Array.from(
      new Set(filteredRegistrations.map(s => s.email.trim()).filter(Boolean))
    );
    if (emails.length === 0) return;

    let textToCopy = "";
    if (separator === ",") {
      textToCopy = emails.join(", ");
    } else if (separator === ";") {
      textToCopy = emails.join("; ");
    } else {
      textToCopy = emails.join("\n");
    }

    navigator.clipboard.writeText(textToCopy);
    setCopiedFormat(separator);
    setTimeout(() => setCopiedFormat(null), 2500);
  };

  // Copy phone numbers
  const handleCopyPhones = () => {
    const phones = Array.from(
      new Set(filteredRegistrations.map(s => s.phone.trim()).filter(Boolean))
    );
    if (phones.length === 0) return;
    navigator.clipboard.writeText(phones.join(", "));
    setCopiedFormat("phone");
    setTimeout(() => setCopiedFormat(null), 2500);
  };

  // Export to Excel CSV (semicolon separated with UTF-8 BOM)
  const handleExportCSV = () => {
    const eventName = selectedEventFilter === "all" ? "Alle_Events" : selectedEventFilter.replace(/\s+/g, "_");
    const headers = [
      "Navn", 
      "E-mail", 
      "Telefon", 
      "Adresse", 
      "Arrangement", 
      "Voksne", 
      "Børn", 
      "Total Personer", 
      "Tilmeldingsdato", 
      "Kilde"
    ];

    const rows = filteredRegistrations.map(item => [
      `"${(item.name || "").replace(/"/g, '""')}"`,
      `"${(item.email || "").replace(/"/g, '""')}"`,
      `"${(item.phone || "").replace(/"/g, '""')}"`,
      `"${(item.address || "").replace(/"/g, '""')}"`,
      `"${(item.eventTitle || "").replace(/"/g, '""')}"`,
      item.adults ?? 0,
      item.children ?? 0,
      (parseInt(item.adults, 10) || 0) + (parseInt(item.children, 10) || 0),
      `"${item.date || ""}"`,
      `"${item.source || "Hjemmeside"}"`
    ]);

    // Add summary row at the bottom
    const summaryRow = [
      `"TOTAL (SUM)"`,
      `""`,
      `""`,
      `""`,
      `"${filteredRegistrations.length} tilmeldinger"`,
      stats.totalAdults,
      stats.totalChildren,
      stats.totalPersons,
      `"${new Date().toISOString().split("T")[0]}"`,
      `""`
    ];

    const csvContent = "\uFEFF" + [headers.join(";"), ...rows.map(r => r.join(";")), summaryRow.join(";")].join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Kongeengen_Event_Tilmeldinger_${eventName}_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Render Login Screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-forest-900/10 py-16 px-4 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 border border-forest-100 shadow-xl space-y-6">
          <div className="text-center space-y-3">
            <div className="w-16 h-16 bg-forest-100 text-forest-700 rounded-2xl flex items-center justify-center mx-auto shadow-inner">
              <Lock className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-bold text-forest-950 font-display">Bestyrelsesadgang</h1>
            <p className="text-sm text-forest-600">
              Indtast adgangskoden for at se og trække listen over tilmeldte til foreningens events.
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-forest-800 uppercase tracking-wider mb-2" htmlFor="password">
                Adgangskode
              </label>
              <div className="relative">
                <input
                  id="password"
                  type="password"
                  value={passwordInput}
                  onChange={(e) => {
                    setPasswordInput(e.target.value);
                    setAuthError(false);
                  }}
                  placeholder="Indtast bestyrelsens adgangskode"
                  className={`w-full px-4 py-3 pl-11 rounded-xl border bg-forest-50/50 text-sm focus:outline-none focus:ring-2 ${
                    authError 
                      ? 'border-red-300 focus:ring-red-500 bg-red-50/20' 
                      : 'border-forest-200 focus:ring-forest-500'
                  }`}
                  autoFocus
                />
                <KeyRound className="w-5 h-5 text-forest-400 absolute left-3.5 top-3.5" />
              </div>
              {authError && (
                <p className="text-xs text-red-600 mt-2 flex items-center gap-1 font-medium">
                  <AlertCircle className="w-3.5 h-3.5" /> Forkert adgangskode. Prøv igen.
                </p>
              )}
            </div>

            <button
              type="submit"
              className="w-full py-3.5 rounded-xl bg-forest-700 hover:bg-forest-600 text-white font-semibold text-sm transition-all shadow-md hover:shadow-forest-700/20 cursor-pointer"
            >
              Log ind
            </button>
          </form>

          <div className="pt-4 border-t border-forest-100 text-center">
            <Link to="/" className="text-xs font-semibold text-forest-600 hover:text-forest-800">
              ← Tilbage til forsiden
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        
        {/* Navigation Tabs between Newsletter & Events */}
        <div className="bg-white rounded-2xl p-2 border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Link
              to="/nyhedsbrev-modtagere"
              className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors flex items-center gap-2"
            >
              <Mail className="w-4 h-4 text-slate-500" />
              Nyhedsbrev-modtagere
            </Link>
            <Link
              to="/event-tilmeldinger"
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-forest-700 text-white shadow-sm flex items-center gap-2"
            >
              <Users className="w-4 h-4" />
              Event-tilmeldinger & Deltagere
            </Link>
          </div>

          <div className="flex items-center gap-3 pr-2">
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-600 text-xs font-medium transition-colors cursor-pointer"
            >
              <LogOut className="w-3.5 h-3.5" /> Log ud
            </button>
          </div>
        </div>

        {/* Page Header */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="bg-forest-100 text-forest-800 text-xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                  Bestyrelsesværktøj
                </span>
                <span className="text-slate-400 text-xs">•</span>
                <span className="text-slate-500 text-xs">Grundejerforeningen Kongeengen</span>
              </div>
              <h1 className="text-3xl font-bold text-slate-900 font-display">Tilmeldte til Events & Arrangementer</h1>
              <p className="text-sm text-slate-600 max-w-2xl mt-1">
                Få et komplet overblik over alle tilmeldte beboere og husstande til foreningens arrangementer, inklusive automatisk sammentælling af voksne og børn samt nem eksport til Excel.
              </p>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap items-center gap-2.5">
              <button
                onClick={handleOpenAdd}
                className="px-4 py-2.5 rounded-xl bg-forest-700 hover:bg-forest-600 text-white text-xs sm:text-sm font-semibold flex items-center gap-2 shadow-sm transition-all cursor-pointer"
              >
                <UserPlus className="w-4 h-4" /> Tilføj tilmelding
              </button>

              <button
                onClick={handleExportCSV}
                className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs sm:text-sm font-semibold flex items-center gap-2 shadow-sm transition-all cursor-pointer"
                title="Download fuld liste som Excel-kompatibel CSV"
              >
                <FileSpreadsheet className="w-4 h-4" /> Eksporter til Excel
              </button>
            </div>
          </div>
        </div>

        {/* Stat Cards - Sum of Adults, Children, Total Persons & Households */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-200 shadow-sm space-y-1">
            <div className="flex items-center justify-between text-slate-500 text-xs font-semibold uppercase tracking-wider">
              <span>Samlet Personantal</span>
              <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <Users className="w-4 h-4" />
              </div>
            </div>
            <p className="text-3xl sm:text-4xl font-extrabold text-slate-900 font-display">
              {stats.totalPersons}
            </p>
            <p className="text-xs text-slate-500">
              Voksne + børn for valgt visning
            </p>
          </div>

          <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-200 shadow-sm space-y-1">
            <div className="flex items-center justify-between text-slate-500 text-xs font-semibold uppercase tracking-wider">
              <span>Voksne i alt</span>
              <div className="w-8 h-8 rounded-lg bg-forest-50 text-forest-700 flex items-center justify-center">
                <UserCheck className="w-4 h-4" />
              </div>
            </div>
            <p className="text-3xl sm:text-4xl font-extrabold text-forest-900 font-display">
              {stats.totalAdults}
            </p>
            <p className="text-xs text-forest-600 font-medium">
              Voksne deltagere i alt
            </p>
          </div>

          <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-200 shadow-sm space-y-1">
            <div className="flex items-center justify-between text-slate-500 text-xs font-semibold uppercase tracking-wider">
              <span>Børn i alt</span>
              <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                <Baby className="w-4 h-4" />
              </div>
            </div>
            <p className="text-3xl sm:text-4xl font-extrabold text-amber-900 font-display">
              {stats.totalChildren}
            </p>
            <p className="text-xs text-amber-600 font-medium">
              Børn tilmeldt arrangementet
            </p>
          </div>

          <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-200 shadow-sm space-y-1">
            <div className="flex items-center justify-between text-slate-500 text-xs font-semibold uppercase tracking-wider">
              <span>Tilmeldinger</span>
              <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <Home className="w-4 h-4" />
              </div>
            </div>
            <p className="text-3xl sm:text-4xl font-extrabold text-slate-900 font-display">
              {stats.totalRegistrations}
            </p>
            <p className="text-xs text-slate-500">
              Husstande / grupper tilmeldt
            </p>
          </div>
        </div>

        {/* Filter and Action Bar */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm space-y-4">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
            
            {/* Event Filter Pills / Select */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 max-w-full">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider shrink-0 flex items-center gap-1 mr-1">
                <Filter className="w-3.5 h-3.5" /> Event:
              </span>
              <button
                onClick={() => setSelectedEventFilter("all")}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  selectedEventFilter === "all"
                    ? "bg-slate-900 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                Alle arrangementer ({registrations.length})
              </button>
              {eventOptions.map((title) => {
                const count = registrations.filter(r => r.eventTitle === title).length;
                return (
                  <button
                    key={title}
                    onClick={() => setSelectedEventFilter(title)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
                      selectedEventFilter === title
                        ? "bg-forest-700 text-white shadow-sm"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {title} ({count})
                  </button>
                );
              })}
            </div>

            {/* Search Box */}
            <div className="relative w-full lg:w-72">
              <input
                type="text"
                placeholder="Søg i navn, e-mail, tlf, adresse..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
              />
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600 text-xs"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Secondary Action Toolbar: Copy Emails & Phones */}
          <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3 text-xs">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-slate-500">Kopier adresser:</span>
              <button
                onClick={() => handleCopyEmails(",")}
                className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                {copiedFormat === "," ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                {copiedFormat === "," ? "Kopieret (komma)!" : "Komma (Gmail)"}
              </button>
              <button
                onClick={() => handleCopyEmails(";")}
                className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                {copiedFormat === ";" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                {copiedFormat === ";" ? "Kopieret (semikolon)!" : "Semikolon (Outlook)"}
              </button>
              <button
                onClick={handleCopyPhones}
                className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                {copiedFormat === "phone" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Phone className="w-3.5 h-3.5" />}
                {copiedFormat === "phone" ? "Telefonnumre kopieret!" : "Kopier telefonnumre"}
              </button>
            </div>

            <div className="text-slate-500 font-medium">
              Viser {filteredRegistrations.length} tilmeldinger ({stats.totalPersons} personer i alt)
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs sm:text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="py-3.5 px-4 sm:px-6">Deltager / Navn</th>
                  <th className="py-3.5 px-4">Kontaktoplysninger</th>
                  <th className="py-3.5 px-4">Adresse</th>
                  <th className="py-3.5 px-4">Arrangement</th>
                  <th className="py-3.5 px-3 text-center">Voksne</th>
                  <th className="py-3.5 px-3 text-center">Børn</th>
                  <th className="py-3.5 px-3 text-center">I alt</th>
                  <th className="py-3.5 px-4 text-center">Dato</th>
                  <th className="py-3.5 px-4 text-right">Handlinger</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRegistrations.length > 0 ? (
                  filteredRegistrations.map((item) => {
                    const totalForEntry = (parseInt(item.adults, 10) || 0) + (parseInt(item.children, 10) || 0);
                    return (
                      <tr key={item.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-3.5 px-4 sm:px-6">
                          <div className="font-bold text-slate-900">{item.name}</div>
                          <span className="text-[11px] text-slate-400">{item.source || "Hjemmeside"}</span>
                        </td>
                        <td className="py-3.5 px-4">
                          <div className="text-slate-800 font-medium">{item.email}</div>
                          {item.phone && <div className="text-slate-500 text-xs">{item.phone}</div>}
                        </td>
                        <td className="py-3.5 px-4 text-slate-600">
                          {item.address || "—"}
                        </td>
                        <td className="py-3.5 px-4">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-forest-50 text-forest-800 border border-forest-100">
                            <Calendar className="w-3 h-3 text-forest-600" />
                            {item.eventTitle}
                          </span>
                        </td>
                        <td className="py-3.5 px-3 text-center font-bold text-forest-800">
                          {item.adults}
                        </td>
                        <td className="py-3.5 px-3 text-center font-bold text-amber-700">
                          {item.children}
                        </td>
                        <td className="py-3.5 px-3 text-center">
                          <span className="inline-block px-2 py-0.5 rounded-md font-extrabold text-xs bg-slate-100 text-slate-800">
                            {totalForEntry} pers.
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-center text-slate-500 text-xs">
                          {item.date}
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => handleOpenEdit(item)}
                              className="p-1.5 text-slate-400 hover:text-forest-700 hover:bg-forest-50 rounded-lg transition-colors cursor-pointer"
                              title="Rediger tilmelding"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                              title="Slet tilmelding"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="py-12 text-center text-slate-500">
                      <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="font-semibold text-slate-700">Ingen tilmeldinger fundet</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {searchTerm ? "Ingen tilmeldinger matchede din søgning." : "Der er endnu ingen tilmeldinger til dette arrangement."}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
              {filteredRegistrations.length > 0 && (
                <tfoot className="bg-slate-100/80 border-t-2 border-slate-200 font-bold text-slate-800 text-xs sm:text-sm">
                  <tr>
                    <td colSpan="4" className="py-3.5 px-4 sm:px-6">
                      SAMLET SUM ({filteredRegistrations.length} tilmeldinger)
                    </td>
                    <td className="py-3.5 px-3 text-center text-forest-800 font-extrabold">
                      {stats.totalAdults} voksne
                    </td>
                    <td className="py-3.5 px-3 text-center text-amber-800 font-extrabold">
                      {stats.totalChildren} børn
                    </td>
                    <td className="py-3.5 px-3 text-center font-extrabold text-slate-950">
                      {stats.totalPersons} i alt
                    </td>
                    <td colSpan="2" className="py-3.5 px-4 text-right text-xs text-slate-500 font-normal">
                      Kongeengen
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* Info card about automatic sync */}
        <div className="bg-forest-50/50 rounded-2xl p-5 border border-forest-100 text-xs text-forest-800 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-forest-600 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="font-bold">Automatisk opsamling og summering af event-tilmeldinger</p>
            <p className="text-forest-700 leading-relaxed">
              Når beboere udfylder tilmeldingsformularen på et specifikt event (f.eks. Sommerfest eller Arbejdsdag), gemmes tilmeldingen automatisk her med opgørelse af voksne og børn. Du kan til enhver tid trække en opdateret Excel-fil eller kopiere alle e-mails for at sende fællesinformation ud til de tilmeldte.
            </p>
          </div>
        </div>

      </div>

      {/* Add / Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl border border-slate-200 space-y-6">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="text-xl font-bold text-slate-900 font-display">
                {editingItem ? "Rediger tilmelding" : "Tilføj ny tilmelding manuelt"}
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveModal} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Arrangement *
                </label>
                <input
                  type="text"
                  required
                  value={formData.eventTitle}
                  onChange={(e) => setFormData({ ...formData, eventTitle: e.target.value })}
                  placeholder="F.eks. Sommerfest 2026"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    Fulde navn *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Beboerens navn"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    E-mailadresse *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="mail@adresse.dk"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    Telefonnummer
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="12 34 56 78"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    Adresse
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="F.eks. Kongevænget 12"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 rounded-xl border border-slate-100">
                <div>
                  <label className="block text-xs font-bold text-forest-800 uppercase tracking-wider mb-1.5">
                    Antal voksne *
                  </label>
                  <input
                    type="number"
                    min="0"
                    required
                    value={formData.adults}
                    onChange={(e) => setFormData({ ...formData, adults: parseInt(e.target.value, 10) || 0 })}
                    className="w-full px-3.5 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-forest-500 font-bold text-forest-800"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-amber-800 uppercase tracking-wider mb-1.5">
                    Antal børn
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.children}
                    onChange={(e) => setFormData({ ...formData, children: parseInt(e.target.value, 10) || 0 })}
                    className="w-full px-3.5 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-forest-500 font-bold text-amber-800"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-semibold cursor-pointer"
                >
                  Annuller
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl bg-forest-700 hover:bg-forest-600 text-white text-xs font-semibold shadow-sm cursor-pointer"
                >
                  {editingItem ? "Gem ændringer" : "Gem tilmelding"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
